import { useRef } from "react";
import { useFormContext } from "react-hook-form";

import { ProgressCircle } from "@smart/itops-sb-design-system-dom";
import { useDOMCapture } from "@smart/manage-hooks-dom";

import { BrandingPreviewFormContainer } from "./form-container";
import { BrandingInput } from "../types";

type BrandingPreviewProps = {
  team:
    | {
        uri: string;
        name: string;
        picture?: string | null;
      }
    | undefined;
  firmLogoDataUrl?: string | null;
};

export const BrandingPreview = ({
  team,
  firmLogoDataUrl,
}: BrandingPreviewProps) => {
  const formContainerRef = useRef<HTMLDivElement>(null);
  const { watch } = useFormContext();
  const branding: BrandingInput = watch();

  const { screenshot, capturing, error, clear } = useDOMCapture({
    containerRef: formContainerRef,
    dependencyKey: JSON.stringify(branding),
    options: { imageSelector: 'img[aria-label*="Logo"]' },
  });

  return (
    <div className="flex flex-col gap-small p-[2.8rem] bg-neutral-20 rounded-tr rounded-br h-full">
      <div className="text-paragraph font-semibold">Preview</div>
      <div className="relative flex-1 min-h-0 w-full">
        <BrandingPreviewFormContainer
          branding={branding}
          containerRef={formContainerRef}
          team={team}
          firmLogoDataUrl={firmLogoDataUrl}
        />
        {capturing && (
          <div className="flex items-center justify-center h-full w-full">
            <ProgressCircle />
          </div>
        )}
        {!capturing && screenshot && (
          <div className="w-full h-full flex items-center justify-center overflow-auto bg-neutral-100 border border-neutral-150 rounded">
            <img
              src={screenshot}
              alt="Branding Preview"
              className="block h-full w-full object-contain box-border"
              onError={clear}
            />
          </div>
        )}
        {!capturing && !screenshot && !error && (
          <div className="flex items-center justify-center h-full w-full">
            <ProgressCircle />
          </div>
        )}
        {error && (
          <div className="flex items-center justify-center h-full w-full text-paragraph font-semibold">
            Preview is unavailable
          </div>
        )}
      </div>
    </div>
  );
};
